(() => {
  window.SBHeaderInitializers = window.SBHeaderInitializers || [];

  window.SBHeaderInitializers.push((context) => {
    const { header, signal, addRuntimeEventListener, onAbort } = context;
    const { shared } = context;

    const announcementElement = document.querySelector('.header__announcement');
    const headerInnerElement = context.findHeaderRef(header, 'headerInner', '.header__inner');
    const headerSectionElement = header.closest('.header-section');
    const getDesktopAccessorySurface = () => document.querySelector('[data-sb-header-accessory-surface]');

    const getScrollTop = () => document.scrollingElement?.scrollTop ?? 0;
    const getMaxScrollTop = () => {
      const scrollingElement = document.scrollingElement;
      if (!(scrollingElement instanceof Element)) return 0;
      return Math.max(0, scrollingElement.scrollHeight - window.innerHeight);
    };
    const clampViewportY = (value) => Math.max(0, Math.min(window.innerHeight, value));
    const bottomBounceThresholdPx = 16;
    const stickyHideThresholdPx = 4;
    const isDesktopViewport = () => window.matchMedia('(min-width: 990px)').matches;
    const isMobileViewport = () => window.matchMedia('(max-width: 989px)').matches;
    const hasElement = (selector) => document.querySelector(selector) instanceof HTMLElement;
    const getProductRoot = () => document.querySelector('.sb-product-main:not(.sb-product-main--quick-add)');

    const COLLAPSE_DISTANCE = 60;
    /* Exponential approach horizon: 99.9% of the way in this many ms
       (time constant ~46ms, 95% caught up in ~140ms). Long enough that
       chunky main-thread ticks read as one continuous glide, short
       enough that a slow deliberate scrub still feels attached. */
    const COLLAPSE_SMOOTH_DURATION_MS = 320;
    const FUSED_ACCESSORY_SELECTOR = '[data-sb-header-accessory]';
    const resolveNavCollapseTargets = () => {
      const targets = new Set([header]);
      document.querySelectorAll(FUSED_ACCESSORY_SELECTOR).forEach((accessoryElement) => {
        const target = accessoryElement.closest('.shopify-section') || accessoryElement;
        if (target instanceof HTMLElement) targets.add(target);
      });
      // Sticky sidebar cards follow the same scrub without their own scroll loop.
      document.querySelectorAll('.sb-blog-overview__sidebar-card-wrap, .sb-blog-post__sidebar-card-wrap')
        .forEach((card) => targets.add(card));
      return [...targets];
    };
    let navCollapseTargets = resolveNavCollapseTargets();
    let hasFusedAccessoryBar = hasElement(FUSED_ACCESSORY_SELECTOR);
    const initialScrollTop = getScrollTop();
    const navigationType = performance.getEntriesByType?.('navigation')?.[0]?.type || 'navigate';
    let navCollapse = 0;
    let navCollapseTarget = 0;
    let navCollapseAnimationRaf = null;
    let navCollapseAnimationLastTime = 0;
    let navCollapseStartupRaf = null;

    const clampNavCollapse = (value) => Math.max(0, Math.min(COLLAPSE_DISTANCE, value));

    const setNavCollapseStyle = (propertyName, value) => {
      navCollapseTargets.forEach((target) => {
        if (target.style.getPropertyValue(propertyName) !== value) {
          target.style.setProperty(propertyName, value);
        }
      });
    };

    const applyNavCollapse = (value) => {
      navCollapse = clampNavCollapse(value);
      const nextState = navCollapse <= 0
        ? 'expanded'
        : (navCollapse >= COLLAPSE_DISTANCE ? 'collapsed' : 'collapsing');
      const previousState = header.dataset.navCollapseState;
      if (header.dataset.navCollapseState !== nextState) {
        header.dataset.navCollapseState = nextState;
      }
      setNavCollapseStyle('--sb-nav-collapse', `${navCollapse.toFixed(1)}px`);
      setNavCollapseStyle('--sb-nav-collapse-progress', (navCollapse / COLLAPSE_DISTANCE).toFixed(3));
      /* Header popouts need the final visible stack edge, not geometry
         reads on every scroll frame. Re-measure once the
         transform reaches either stable endpoint. */
      if (previousState !== nextState && nextState !== 'collapsing') {
        scheduleTopStackSync();
      }
    };

    const cancelNavCollapseAnimation = () => {
      if (navCollapseAnimationRaf != null) {
        window.cancelAnimationFrame(navCollapseAnimationRaf);
        navCollapseAnimationRaf = null;
      }
      navCollapseAnimationLastTime = 0;
    };

    const animateNavCollapse = (timestamp) => {
      navCollapseAnimationRaf = null;
      const previousTimestamp = navCollapseAnimationLastTime || (timestamp - (1000 / 60));
      const elapsed = Math.max(1, Math.min(34, timestamp - previousTimestamp));
      navCollapseAnimationLastTime = timestamp;
      const remaining = navCollapseTarget - navCollapse;

      if (Math.abs(remaining) <= 0.1) {
        applyNavCollapse(navCollapseTarget);
        navCollapseAnimationLastTime = 0;
        return;
      }

      /* Reach 99.9% of the target in the declared duration. This keeps a
         fast wheel/touchpad jump from skipping the fold while remaining
         frame-rate independent and requiring no layout reads. */
      const progress = 1 - Math.pow(0.001, elapsed / COLLAPSE_SMOOTH_DURATION_MS);
      applyNavCollapse(navCollapse + (remaining * progress));
      navCollapseAnimationRaf = window.requestAnimationFrame(animateNavCollapse);
    };

    const setNavCollapseTarget = (value, { smooth = false } = {}) => {
      navCollapseTarget = clampNavCollapse(value);
      if (!smooth || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
        cancelNavCollapseAnimation();
        applyNavCollapse(navCollapseTarget);
        return;
      }
      if (navCollapseAnimationRaf != null) return;
      navCollapseAnimationLastTime = 0;
      navCollapseAnimationRaf = window.requestAnimationFrame(animateNavCollapse);
    };

    const clearNavCollapseStyles = () => {
      cancelNavCollapseAnimation();
      navCollapse = 0;
      navCollapseTarget = 0;
      navCollapseTargets.forEach((target) => {
        target.style.removeProperty('--sb-nav-collapse');
        target.style.removeProperty('--sb-nav-collapse-progress');
      });
      // Clear values left by the previous document-wide implementation.
      document.documentElement.style.removeProperty('--sb-nav-collapse');
      document.documentElement.style.removeProperty('--sb-nav-collapse-progress');
    };

    const syncNavCollapse = ({ scrollTop, previousScrollTop, isBottomBounce = false }) => {
      const enabled = stickyMode === 'scroll-up' && hasFusedAccessoryBar;
      if (!enabled) {
        if (header.dataset.navCollapseState) {
          delete header.dataset.navCollapseState;
          clearNavCollapseStyles();
        }
        return;
      }
      /* The fold rides with the very first scrolled pixel, in parallel
         with the announcement bar scrolling away. Gating it on the header
         being pinned first read as two sequential motions on a fast flick
         (brief travel, then a late fold), and the pin threshold itself was
         a cached measurement that could corrupt. The tether below caps the
         fold at the distance actually scrolled from the top - that alone
         keeps it fully open at rest and immune to rubber-band deltas. */
      const clampedScrollTop = Math.max(0, scrollTop);
      const clampedPreviousScrollTop = Math.max(0, previousScrollTop);
      let nextCollapse = navCollapseTarget;
      if (!isBottomBounce) {
        nextCollapse = clampNavCollapse(navCollapseTarget + (clampedScrollTop - clampedPreviousScrollTop));
      }
      nextCollapse = Math.min(nextCollapse, clampedScrollTop);
      /* The scrub target accumulates 1:1 with the sampled scroll offset,
         but the RENDERED fold always glides toward it through the same
         short exponential the programmatic paths use. The page scroll is
         composited while this fold is main-thread-bound: under load the
         loop may only tick a few times per gesture, and a strictly direct
         scrub then teleports the fold by each accumulated chunk - the
         "3-4 jumps per fold" feel. The ever-present glide bridges those
         gaps and settles at full frame rate once the thread frees up.
         Single mode at every speed - never switch between direct and
         eased mid-gesture, which is what made the old hybrid feel
         unpredictable. */
      setNavCollapseTarget(nextCollapse, { smooth: true });
    };

    const expandNavCollapseForOverlay = () => {
      if (stickyMode !== 'scroll-up' || !hasFusedAccessoryBar || (navCollapse <= 0 && navCollapseTarget <= 0)) return;
      setNavCollapseTarget(0, { smooth: false });
      scheduleTopStackSync();
    };

    const resolveResponsiveStickyMode = () => {
      if (hasElement('.sb-blog-overview') || hasElement('.sb-blog-post')) {
        return 'scroll-up';
      }

      if (isDesktopViewport() && hasElement('.sb-metronome-app-sticky-nav')) {
        return 'scroll-up';
      }

      if (isMobileViewport() && hasElement('.sb-manual-chapters')) {
        return 'scroll-up';
      }

      const productRoot = getProductRoot();
      if (productRoot instanceof HTMLElement) {
        const hasSegmentedTopBar = !productRoot.classList.contains('sb-product-main--no-page-header');
        if (hasSegmentedTopBar) {
          return 'scroll-up';
        }

        if (isDesktopViewport()) {
          return 'scroll-up';
        }
      }

      return 'always';
    };

    let stickyMode = resolveResponsiveStickyMode();

    header.dataset.stickyMode = stickyMode;
    if (headerSectionElement instanceof HTMLElement) {
      headerSectionElement.dataset.stickyMode = stickyMode;
      headerSectionElement.dataset.stickyState = header.dataset.stickyState || 'inactive';
    }
    header.setAttribute('sticky', stickyMode);

    let lastY = getScrollTop();
    let offscreen = false;
    let isScrollSyncSuspended = false;
    let stickyIntersectionObserver = null;
    let topStackSyncRaf = null;
    let scrollRafId = null;
    let deferredTopStackSyncTimer = null;
    let headerHeightObserver = null;
    let lastStickyActiveScrollTop = getScrollTop();
    let lastViewportWidth = window.innerWidth;
    let lastViewportHeight = window.innerHeight;
    let lastObservedScrollDirection = 'none';
    let naturalHeaderTop = Math.max(
      0,
      (headerSectionElement || header).getBoundingClientRect().top + getScrollTop()
    );
    const readHeaderSpacingVar = (propertyName, fallback = 0) => {
      const parsed = Number.parseFloat(getComputedStyle(header).getPropertyValue(propertyName));
      return Number.isFinite(parsed) ? parsed : fallback;
    };

    const minimumAnnouncementHeight = () => readHeaderSpacingVar('--sb-announcement-min-height', 0);
    const announcementHeight = () => {
      const measuredHeight = announcementElement ? announcementElement.getBoundingClientRect().height || 0 : 0;
      return Math.max(measuredHeight, minimumAnnouncementHeight(), 0);
    };

    const setStickyState = (nextState) => {
      const normalizedState = nextState === 'active' || nextState === 'idle' ? nextState : 'inactive';
      const currentState = headerSectionElement instanceof HTMLElement
        ? headerSectionElement.dataset.stickyState
        : header.dataset.stickyState;
      if (currentState === normalizedState && header.dataset.stickyState === normalizedState) return false;
      if (headerSectionElement instanceof HTMLElement) {
        headerSectionElement.dataset.stickyState = normalizedState;
      }
      header.dataset.stickyState = normalizedState;
      return true;
    };

    const getStickyState = () => (
      headerSectionElement instanceof HTMLElement
        ? headerSectionElement.dataset.stickyState || 'inactive'
        : header.dataset.stickyState || 'inactive'
    );

    const setScrollDirection = (nextDirection) => {
      const normalizedDirection = nextDirection === 'up' || nextDirection === 'down' ? nextDirection : 'none';
      if (header.dataset.scrollDirection !== normalizedDirection) {
        header.dataset.scrollDirection = normalizedDirection;
      }
    };

    const syncTopStackBottom = () => {
      const root = document.documentElement;
      if (!(root instanceof HTMLElement)) return;

      const nextAnnouncementHeight = announcementHeight();
      const nextAnnouncementHeightValue = `${nextAnnouncementHeight}px`;
      if (root.style.getPropertyValue('--sb-announcement-height') !== nextAnnouncementHeightValue) {
        root.style.setProperty('--sb-announcement-height', nextAnnouncementHeightValue);
      }

      const headerRect = header.getBoundingClientRect();
      const nextBottom = clampViewportY(headerRect.bottom);
      const nextBottomValue = `${nextBottom}px`;
      if (root.style.getPropertyValue('--sb-top-stack-bottom') !== nextBottomValue) {
        root.style.setProperty('--sb-top-stack-bottom', nextBottomValue);
      }

      let nextContentBottom = nextBottom;
      if (headerInnerElement instanceof HTMLElement) {
        const innerBottom = clampViewportY(headerInnerElement.getBoundingClientRect().bottom);
        const announcementBottom = announcementElement instanceof HTMLElement
          ? clampViewportY(announcementElement.getBoundingClientRect().bottom)
          : nextBottom;
        nextContentBottom = Math.max(announcementBottom, innerBottom);
      }
      const nextContentBottomValue = `${nextContentBottom}px`;
      if (root.style.getPropertyValue('--sb-top-stack-content-bottom') !== nextContentBottomValue) {
        root.style.setProperty('--sb-top-stack-content-bottom', nextContentBottomValue);
      }

      if (!isDesktopViewport() || !(headerInnerElement instanceof HTMLElement)) {
        root.style.removeProperty('--sb-header-popout-anchor-bottom');
        return;
      }

      const headerInnerRect = headerInnerElement.getBoundingClientRect();
      const accessorySurface = getDesktopAccessorySurface();
      const accessoryRect = accessorySurface instanceof HTMLElement
        ? accessorySurface.getBoundingClientRect()
        : null;
      const hasVisibleAccessory = accessorySurface instanceof HTMLElement
        && accessorySurface.getClientRects().length > 0
        && accessoryRect
        && accessoryRect.height > 0
        && accessoryRect.bottom > headerInnerRect.bottom;
      const popoutAnchorBottom = clampViewportY(
        hasVisibleAccessory ? accessoryRect.bottom : headerInnerRect.bottom,
      );
      const popoutAnchorBottomValue = `${popoutAnchorBottom.toFixed(2)}px`;

      if (root.style.getPropertyValue('--sb-header-popout-anchor-bottom') !== popoutAnchorBottomValue) {
        root.style.setProperty('--sb-header-popout-anchor-bottom', popoutAnchorBottomValue);
      }
      // Remove the current collapse from the measured edge. Sidebars subtract
      // the continuously animated value, rather than following endpoint updates.
      const expandedAnchorBottomValue = `${(popoutAnchorBottom + navCollapse).toFixed(2)}px`;
      if (root.style.getPropertyValue('--sb-header-expanded-anchor-bottom') !== expandedAnchorBottomValue) {
        root.style.setProperty('--sb-header-expanded-anchor-bottom', expandedAnchorBottomValue);
      }
    };

    const applyStickyVisualState = ({ syncTopStack = true } = {}) => {
      if (syncTopStack) syncTopStackBottom();
    };

    const scheduleTopStackSync = () => {
      if (signal.aborted) return;
      if (topStackSyncRaf != null) {
        window.cancelAnimationFrame(topStackSyncRaf);
      }
      topStackSyncRaf = window.requestAnimationFrame(() => {
        topStackSyncRaf = null;
        applyStickyVisualState();
      });
    };

    const syncStickyObserver = () => {
      if (stickyIntersectionObserver) {
        stickyIntersectionObserver.disconnect();
        stickyIntersectionObserver = null;
      }

      const alwaysSticky = stickyMode === 'always';
      stickyIntersectionObserver = new IntersectionObserver(
        ([entry]) => {
          if (!entry) return;
          if (alwaysSticky) {
            const stateChanged = setStickyState(entry.isIntersecting ? 'inactive' : 'active');
            if (stateChanged) scheduleTopStackSync();
            return;
          }
          const nextOffscreen = !entry.isIntersecting
            || getStickyState() === 'active'
            || getStickyState() === 'idle';
          if (nextOffscreen === offscreen) return;
          offscreen = nextOffscreen;
          /* Crossing the natural header boundary can happen after the scroll
             frame that triggered the observer. Resolve the state immediately
             so the detached accessory never waits for another scroll event. */
          syncStickyState();
        },
        {
          rootMargin: alwaysSticky ? '-1px 0px 0px 0px' : '0px',
          threshold: alwaysSticky ? 1 : 0,
        }
      );
      stickyIntersectionObserver.observe(header);
    };

    const syncStickyState = ({ force = false } = {}) => {
      const scrollTop = getScrollTop();
      const alwaysSticky = stickyMode === 'always';
      const maxScrollTop = alwaysSticky ? 0 : getMaxScrollTop();
      if (
        !alwaysSticky
        && getStickyState() !== 'active'
        && scrollTop <= naturalHeaderTop + 0.5
      ) {
        naturalHeaderTop = Math.max(
          0,
          (headerSectionElement || header).getBoundingClientRect().top + scrollTop
        );
      }
      const isNearBottom = maxScrollTop > 0 && scrollTop >= (maxScrollTop - bottomBounceThresholdPx);
      const wasNearBottom = maxScrollTop > 0 && lastY >= (maxScrollTop - bottomBounceThresholdPx);
      const isBottomBounce = stickyMode === 'scroll-up' && isNearBottom && wasNearBottom;
      /* A revealed sticky header sits at viewport top by definition. Reading
         its rendered rect here made the next upward scroll frame mistake that
         sticky position for the header's natural document position, toggling
         active -> inactive -> active. Track the natural flow position instead. */
      const isAtTop = alwaysSticky
        ? getStickyState() !== 'active'
        : scrollTop <= naturalHeaderTop + 0.5;
      const isScrollingUp = !isBottomBounce && scrollTop < lastY;
      const isScrollingDown = !isBottomBounce && scrollTop > lastY;
      if (isScrollingUp) {
        lastObservedScrollDirection = 'up';
      } else if (isScrollingDown) {
        lastObservedScrollDirection = 'down';
      }

      syncNavCollapse({ scrollTop, previousScrollTop: lastY, isBottomBounce });

      if (!offscreen && stickyMode !== 'always' && !force) {
        lastY = scrollTop;
        return;
      }

      if (stickyMode === 'always') {
        if (isAtTop) {
          lastStickyActiveScrollTop = scrollTop;
          setScrollDirection('none');
        } else if (isScrollingUp) {
          lastStickyActiveScrollTop = scrollTop;
          setScrollDirection('up');
        } else {
          setScrollDirection('down');
        }
        lastY = scrollTop;
        applyStickyVisualState({ syncTopStack: false });
        return;
      }

      if (force) {
        if (isAtTop) {
          offscreen = false;
          lastStickyActiveScrollTop = scrollTop;
          setStickyState('inactive');
          setScrollDirection('none');
        } else if (stickyMode === 'scroll-up' && getStickyState() === 'active') {
          offscreen = true;
          lastStickyActiveScrollTop = scrollTop;
          setStickyState('active');
          setScrollDirection('up');
        } else {
          offscreen = true;
          setStickyState('idle');
          setScrollDirection('none');
        }
        lastY = scrollTop;
        applyStickyVisualState();
        return;
      }

      if (isScrollingUp) {
        if (isAtTop) {
          offscreen = false;
          lastStickyActiveScrollTop = scrollTop;
          setStickyState('inactive');
          setScrollDirection('none');
        } else {
          lastStickyActiveScrollTop = scrollTop;
          setStickyState('active');
          setScrollDirection('up');
        }
      } else if (getStickyState() === 'active') {
        const hasScrolledDownEnoughToHide = scrollTop >= (lastStickyActiveScrollTop + stickyHideThresholdPx);
        if (hasScrolledDownEnoughToHide) {
          setScrollDirection('down');
          setStickyState('idle');
        } else {
          setScrollDirection('up');
        }
      } else {
        setStickyState('idle');
        setScrollDirection('none');
      }

      lastY = scrollTop;
      applyStickyVisualState();
    };

    const refreshHeaderScrollMode = ({ forceSync = false } = {}) => {
      navCollapseTargets = resolveNavCollapseTargets();
      hasFusedAccessoryBar = hasElement(FUSED_ACCESSORY_SELECTOR);
      stickyMode = resolveResponsiveStickyMode();
      header.dataset.stickyMode = stickyMode;
      if (headerSectionElement instanceof HTMLElement) {
        headerSectionElement.dataset.stickyMode = stickyMode;
      }
      header.setAttribute('sticky', stickyMode);
      syncStickyObserver();
      if (forceSync) syncStickyState({ force: true });
      else applyStickyVisualState();
      scheduleTopStackSync();
    };

    const setScrollSyncSuspended = (suspended) => {
      isScrollSyncSuspended = Boolean(suspended);
      lastY = getScrollTop();
    };

    const setLastScrollY = (value) => {
      lastY = Math.max(0, Number.isFinite(value) ? value : getScrollTop());
    };

    const captureVisualState = () => ({
      stickyMode,
      stickyState: getStickyState(),
      scrollDirection: header.dataset.scrollDirection || 'none',
      offscreen,
      navCollapse,
      navCollapseTarget,
    });

    const restoreVisualState = (snapshot) => {
      if (!snapshot || snapshot.stickyMode !== stickyMode) return;
      offscreen = Boolean(snapshot.offscreen);
      setStickyState(snapshot.stickyState);
      setScrollDirection(snapshot.scrollDirection);
      setLastScrollY(getScrollTop());
      if (header.dataset.navCollapseState && Number.isFinite(snapshot.navCollapse)) {
        // Segment restoration can land at the top of a shorter page. Never
        // restore a hidden nav where no upward scroll remains to reveal it.
        const scrollLimit = Math.max(0, getScrollTop());
        setNavCollapseTarget(Math.min(snapshot.navCollapse, scrollLimit));
        setNavCollapseTarget(Math.min(snapshot.navCollapseTarget ?? snapshot.navCollapse, scrollLimit), { smooth: true });
      }
      applyStickyVisualState();
    };

    shared.syncStickyState = syncStickyState;
    shared.syncTopStackBottom = syncTopStackBottom;
    shared.scheduleTopStackSync = scheduleTopStackSync;
    shared.refreshHeaderScrollMode = refreshHeaderScrollMode;
    shared.setStickyLastY = setLastScrollY;

    window.SBHeaderNav = {
      ...(window.SBHeaderNav || {}),
      captureVisualState,
      expandNavCollapseForOverlay,
      refreshScrollMode: refreshHeaderScrollMode,
      restoreVisualState,
      setScrollSyncSuspended,
      syncTopStack: syncTopStackBottom,
    };

    /* Establish a painted starting point before the first scroll sync. A fresh
       navigation that has already moved must animate from the server-rendered
       expanded state; a history restoration should render its restored state
       immediately. Keeping this after the stack helpers are initialized also
       lets endpoint consumers synchronize safely. */
    if (hasFusedAccessoryBar) {
      const initialCollapse = clampNavCollapse(initialScrollTop);
      if (navigationType === 'back_forward') {
        setNavCollapseTarget(initialCollapse);
      } else if (initialCollapse > 0) {
        applyNavCollapse(0);
        /* Let the expanded server state paint once before starting the fold.
           Otherwise the first animation callback can run before a paint and
           make a cold-load scroll appear to jump halfway closed. */
        navCollapseStartupRaf = window.requestAnimationFrame(() => {
          navCollapseStartupRaf = null;
          const currentCollapse = clampNavCollapse(getScrollTop());
          setNavCollapseTarget(currentCollapse, { smooth: currentCollapse > 0 });
        });
      } else {
        applyNavCollapse(0);
      }
    }

    syncStickyObserver();
    syncTopStackBottom();
    scheduleTopStackSync();

    /* Sample the scroll offset in a per-frame loop instead of reacting to
       individual scroll events. Event->rAF chains inherit the event
       cadence: engines coalesce or delay scroll events under load
       (WebKit momentum especially), so the scrub would sit still and then
       replay a burst - the "sticks, then jumps" failure. A frame loop
       reads the offset the compositor is about to paint, every frame,
       so the fold tracks it 1:1. The loop parks itself after ~half a
       second without movement; any scroll event re-arms it. */
    const SCROLL_LOOP_IDLE_FRAME_LIMIT = 30;
    let scrollLoopIdleFrames = 0;
    const scrollLoopTick = () => {
      scrollRafId = null;
      if (typeof shared.isScrollLocked === 'function' && shared.isScrollLocked()) {
        const scrollLockY = typeof shared.getScrollLockY === 'function' ? shared.getScrollLockY() : 0;
        lastY = Number.isFinite(scrollLockY) ? scrollLockY : lastY;
        return;
      }
      if (isScrollSyncSuspended) {
        lastY = getScrollTop();
        return;
      }
      if (getScrollTop() !== lastY) {
        scrollLoopIdleFrames = 0;
        syncStickyState();
      } else {
        scrollLoopIdleFrames += 1;
      }
      if (scrollLoopIdleFrames < SCROLL_LOOP_IDLE_FRAME_LIMIT) {
        scrollRafId = window.requestAnimationFrame(scrollLoopTick);
      }
    };
    const onWindowScroll = () => {
      scrollLoopIdleFrames = 0;
      if (scrollRafId == null) {
        scrollRafId = window.requestAnimationFrame(scrollLoopTick);
      }
    };

    addRuntimeEventListener(document, 'scroll', onWindowScroll, { passive: true });
    addRuntimeEventListener(header, 'pointerdown', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (!target.closest('.header__icon-link--account')) return;
      syncTopStackBottom();
    }, { capture: true, passive: true });
    addRuntimeEventListener(header, 'focusin', () => {
      expandNavCollapseForOverlay();
      syncTopStackBottom();
    });
    addRuntimeEventListener(window, 'resize', () => {
      const previousViewportWidth = lastViewportWidth;
      const nextViewportWidth = window.innerWidth;
      const previousViewportHeight = lastViewportHeight;
      const nextViewportHeight = window.innerHeight;
      const isViewportExpandingUpward = stickyMode === 'scroll-up'
        && isMobileViewport()
        && nextViewportWidth === previousViewportWidth
        && nextViewportHeight > previousViewportHeight
        && lastObservedScrollDirection === 'up';
      lastViewportWidth = nextViewportWidth;
      lastViewportHeight = nextViewportHeight;

      if (typeof shared.syncPageScrollLock === 'function') {
        shared.syncPageScrollLock();
      }
      refreshHeaderScrollMode();

      if (isViewportExpandingUpward && getScrollTop() > 0) {
        offscreen = true;
        lastStickyActiveScrollTop = getScrollTop();
        setStickyState('active');
        setScrollDirection('up');
        setLastScrollY(getScrollTop());
        applyStickyVisualState();
      } else if (stickyMode === 'scroll-up' && nextViewportWidth === previousViewportWidth) {
        syncStickyState();
      } else {
        syncStickyState({ force: true });
      }
      scheduleTopStackSync();
    }, { passive: true });

    window.requestAnimationFrame(() => {
      if (signal.aborted) return;
      refreshHeaderScrollMode();
      syncStickyState({ force: true });
      scheduleTopStackSync();
    });

    deferredTopStackSyncTimer = window.setTimeout(scheduleTopStackSync, 300);
    addRuntimeEventListener(window, 'load', scheduleTopStackSync);

    if (document.fonts && typeof document.fonts.ready === 'object' && typeof document.fonts.ready.then === 'function') {
      document.fonts.ready.then(() => {
        scheduleTopStackSync();
      });
    }

    if (window.ResizeObserver) {
      headerHeightObserver = new ResizeObserver(() => {
        scheduleTopStackSync();
      });
      headerHeightObserver.observe(header);
      if (announcementElement instanceof HTMLElement) {
        headerHeightObserver.observe(announcementElement);
      }
    }

    addRuntimeEventListener(document, 'shopify:section:load', () => {
      refreshHeaderScrollMode({ forceSync: true });
      scheduleTopStackSync();
    });
    addRuntimeEventListener(document, 'shopify:section:unload', () => {
      refreshHeaderScrollMode({ forceSync: true });
      scheduleTopStackSync();
    });

    onAbort(() => {
      if (navCollapseStartupRaf != null) {
        window.cancelAnimationFrame(navCollapseStartupRaf);
        navCollapseStartupRaf = null;
      }
      if (deferredTopStackSyncTimer != null) {
        window.clearTimeout(deferredTopStackSyncTimer);
        deferredTopStackSyncTimer = null;
      }

      if (topStackSyncRaf != null) {
        window.cancelAnimationFrame(topStackSyncRaf);
        topStackSyncRaf = null;
      }

      if (scrollRafId != null) {
        window.cancelAnimationFrame(scrollRafId);
        scrollRafId = null;
      }

      cancelNavCollapseAnimation();

      if (headerHeightObserver) {
        headerHeightObserver.disconnect();
        headerHeightObserver = null;
      }

      if (stickyIntersectionObserver) {
        stickyIntersectionObserver.disconnect();
        stickyIntersectionObserver = null;
      }
    });
  });
})();
